/*
 * The copy of the authentication area (spec 01 §4). It lives here, beside the screens it belongs
 * to, because every area keeps its own catalogue and the shell merges them (modules.md §8, S0-PLAN
 * §4); nothing outside an i18n file contains user-facing text.
 *
 * It is exported as `en`, like every other area's catalogue, so the shell's `core/i18n/index.ts`
 * merges it with one import line (modules.md §8).
 *
 * The `error.*` block is the other half of the contract with the server: the backend answers with a
 * locale key and never with prose (protocol.md §1.1), so every key this area's routes can return has
 * a sentence here — the authentication flow (`error.auth.*`), the account and group directory
 * (`error.account.*`), the administration of accounts and bot tokens (`error.admin.*`) and every
 * denial reason the policy layer refuses with (`error.policy.*`). A key that is missing falls back
 * to the wire code's generic text, which is correct but says nothing.
 *
 * Those keys arrive at runtime and are rendered through `errorText`, which passes the error's
 * `args`, so a `{placeholder}` is safe here — unlike in the server block of `core/i18n/en.ts`.
 */

export const en = {
	// ---- the navy panel of the signed-out screens (components/AuthPage.tsx) ----
	// *Place* is a destination inside the product (the switcher's "Places", a forward quote's
	// "in {place}"), so the instance itself is not called one: it is teamBase, the team's own box.
	"auth.panel.claim": "The team’s own home base.",
	"auth.panel.sub": "Channels, threads and direct messages for the seven of us — on our own box.",

	"auth.login.title": "Sign in",
	"auth.login.identifier": "E-mail or handle",
	"auth.login.password": "Password",
	"auth.login.submit": "Sign in",
	"auth.login.lead":
		"Your e-mail address and your password. A new device is asked for a code as well.",
	"auth.login.signedOut": "You are signed out.",
	"auth.login.invalid": "That e-mail, handle or password is not right.",
	"auth.login.deactivated": "This account is deactivated. Please ask the team.",
	"auth.login.throttled": "Too many attempts. The next one is possible in {seconds} s.",
	"auth.login.forgot": "Forgot your password?",

	"auth.code.title": "Enter your code",
	"auth.code.intro":
		"The eight-character code is in the subject of the e-mail to {email}. Type it here — this window is waiting for it.",
	"auth.code.introNoAddress":
		"The eight-character code is in the subject of the e-mail. Type it here — this window is waiting for it.",
	"auth.code.label": "Code from the e-mail",
	"auth.code.hint": "Eight characters, with or without the dash.",
	"auth.code.submit": "Continue",
	"auth.code.invalid": "That code is not right. {left} attempts left.",
	"auth.code.invalidLastTry": "That code is not right. One attempt left.",
	"auth.code.burned": "That code is used up. Please start again with your password.",
	"auth.code.validFor": "The code is valid for another {minutes} minutes.",
	"auth.code.resend": "Send the code again",
	"auth.code.resendIn": "Send the code again ({seconds} s)",
	"auth.code.resent": "The code has been sent again.",
	"auth.code.back": "Back to sign-in",

	"auth.enrol.title": "Set up your account",
	"auth.enrol.checking": "Checking the invitation …",
	"auth.enrol.invited": "You were invited to teamBase. Choose a password, and you are in.",
	"auth.enrol.greeting": "Welcome, {name}. This account belongs to {email}.",
	"auth.enrol.expired":
		"This invitation has expired or has already been used. Please ask the team for a new one.",
	"auth.enrol.linkMissing":
		"This link is missing its key. Please open the whole link from the e-mail again.",
	"auth.enrol.submit": "Set password and sign in",

	"auth.reset.title": "Reset your password",
	"auth.reset.intro": "We will send a link to the address on file.",
	"auth.reset.email": "E-mail address",
	"auth.reset.submit": "Request a link",
	"auth.reset.sent":
		"If there is an account for this address, the link is on its way. Please check your inbox.",
	"auth.reset.setTitle": "Set a new password",
	"auth.reset.setLead": "Setting a new password signs every other device out.",
	"auth.reset.setSubmit": "Set password and sign in",
	"auth.reset.expired":
		"This link has expired or has already been used. You can request a new one here.",
	"auth.reset.signedOutElsewhere": "Every other device has been signed out.",
	"auth.reset.backToLogin": "Back to sign-in",

	"auth.stepup.title": "Please confirm",
	"auth.stepup.passwordIntro": "For safety, your password once more.",
	"auth.stepup.codeIntro": "This step also needs a code by e-mail. It is valid for ten minutes.",
	"auth.stepup.sendCode": "Send the code",
	"auth.stepup.codeSent": "The code is on its way.",
	"auth.stepup.confirm": "Confirm",
	// The same proof asked for inside a form (useStepUpField) rather than in a window over it.
	"auth.stepup.field": "Your password, for safety",
	"auth.stepup.fieldHint": "This change needs your password again.",
	"auth.stepup.missing": "Your password is needed for this.",

	"auth.password.current": "Current password",
	"auth.password.new": "New password",
	"auth.password.repeat": "Repeat the new password",
	// The toggle stands inside the field and is drawn as an eye, so nothing of it is written on
	// screen and this pair is the whole of what it is called.
	"auth.password.show": "Show password",
	"auth.password.hide": "Hide password",
	"auth.password.mismatch": "The two entries are not the same.",
	// The checklist beside a password being chosen: the three rules the server applies
	// (internal/core/auth/password/policy.go), in its own order. The last one says who checks it,
	// because the browser cannot and must not pretend to.
	"auth.password.rule.length": "At least {min} characters — long beats complicated",
	"auth.password.rule.identifier": "Not your own name or address",
	"auth.password.rule.leak": "Not a password from a known leak — checked when you send it",

	// ---- the account entry in the rail (core/shell/AccountMenu.tsx) ----
	"auth.account.signOut": "Sign out",
	"auth.account.signOutEverywhere": "Sign out everywhere",
	"auth.account.security": "Security",

	// ---- E-02: what a browser is called in the two lists (deviceName.ts) ----
	//
	// The server sends the raw user agent and the client names the thing, because these are words a
	// reader sees and the server has no locale. Two tables and one pattern: the browser, the system,
	// and "{browser} on {os}". The proper nouns are spelled the way their makers spell them (macOS,
	// iOS, ChromeOS, Samsung Internet) and a language that keeps the Latin name keeps them as they
	// are; an agent neither table recognises — a crawler, a command-line client — is named as one
	// unrecognised thing rather than guessed at in halves.
	"auth.device.browser.chrome": "Chrome",
	"auth.device.browser.edge": "Edge",
	"auth.device.browser.firefox": "Firefox",
	"auth.device.browser.opera": "Opera",
	"auth.device.browser.safari": "Safari",
	"auth.device.browser.samsung": "Samsung Internet",
	"auth.device.name": "{browser} on {os}",
	"auth.device.os.android": "Android",
	"auth.device.os.chromeos": "ChromeOS",
	"auth.device.os.ios": "iOS",
	"auth.device.os.linux": "Linux",
	"auth.device.os.macos": "macOS",
	"auth.device.os.windows": "Windows",
	"auth.device.unknown": "Unrecognised browser",

	"auth.security.passwordSection": "Change your password",
	"auth.security.passwordChanged":
		"Your password has changed. Every other session has been signed out.",
	"auth.security.sessions": "Signed-in sessions",
	"auth.security.devices": "Trusted devices",
	// ---- K-05: the security screen's rows (VISUAL.md §7.12) ----
	//
	// The two lists are rows and not tables, so the five column headings are three words in one muted
	// line instead: "signed in 22:20 · last active 22:28 · 203.0.113.0/24". They are written in the
	// middle of a sentence and are lower case for that reason. Each group also carries the lead every
	// settings group has, which is where the sentence about what the block decides now lives.
	"auth.security.devices.lead":
		"A browser that has been through the emailed code once is trusted until the date below, and is not asked again.",
	"auth.security.lastActive": "last active",
	"auth.security.password.lead":
		"Changing it signs every other session out. This device stays trusted.",
	"auth.security.sessions.lead":
		"Every browser this account is signed in on, newest first. Ending one takes effect at once.",
	"auth.security.since": "signed in",
	"auth.security.trustedUntil": "trusted until",
	"auth.security.thisSession": "This session",
	"auth.security.thisDevice": "This device",
	"auth.security.signOut": "Sign out",
	"auth.security.signOutOthers": "Sign out everywhere",
	"auth.security.signedOutOthers": "{count} other sessions have been signed out.",
	"auth.security.signedOutOthers.one": "{count} other session has been signed out.",
	"auth.security.signOutSelfTitle": "Sign out of this session?",
	"auth.security.signOutSelfBody":
		"This signs out the current window. You will need to sign in again to carry on.",
	"auth.security.revokeDevice": "Withdraw trust",
	"auth.security.revokeDeviceTitle": "Stop trusting this device?",
	"auth.security.revokeDeviceBody":
		"The next sign-in on this device will need a code from an e-mail again. The device’s sessions will end.",
	"auth.security.nothing": "Nothing here.",

	"error.auth.secretInvalid": "This link has expired or has already been used.",
	"error.auth.passwordWrong": "That password is not right.",
	"error.auth.codeInvalid": "That code is not right.",
	"error.auth.noStepUpCode": "No code has been requested yet.",
	"error.auth.sessionUnknown": "This session does not exist any more.",
	"error.auth.deviceUnknown": "This device does not exist any more.",
	"error.auth.bearerWithCookie": "This request carries two sign-ins. Please use only one.",
	"error.auth.password.tooShort": "That password is too short — at least {min} characters.",
	"error.auth.password.tooLong": "That password is too long.",
	"error.auth.password.tooManyBytes": "That password is too long.",
	"error.auth.password.containsIdentifier":
		"A password may not contain your own name or your own address.",
	"error.auth.password.breached":
		"This password appears in known data leaks. Please pick another one.",

	// An account and a group are the same area's data (spec 01 §11), so the directory's refusals
	// live in the identity catalogue too. The short form of an account or a group is its handle.
	"error.account.unknown": "This account does not exist.",
	"error.account.userIdInvalid": "This account id is not valid.",
	"error.account.emailInvalid": "This does not look like an e-mail address.",
	"error.account.emailTaken": "This address already belongs to an account.",
	"error.account.handleInvalid":
		"This handle does not work. Lower-case letters, digits, dot, dash and underscore are allowed.",
	"error.account.handleReserved": "This handle is reserved.",
	"error.account.handleTaken": "This handle is already taken.",
	"error.account.displayNameInvalid": "This name does not work.",
	"error.account.profileTextInvalid":
		"This text is too long or holds characters that cannot be shown.",
	"error.account.statusEmojiInvalid": "Please pick one of the offered status emoji.",
	"error.account.statusUntilInvalid": "Pick a time that is still ahead and less than a year away.",
	"error.account.localeInvalid": "This language does not exist.",
	"error.account.timezoneInvalid": "This time zone does not exist.",
	"error.account.roleInvalid": "This kind of access does not exist.",
	"error.account.stateInvalid": "This account state does not exist.",
	"error.account.groupNotFound": "This group does not exist.",
	"error.account.groupNameLength": "This group name is too long or too short.",
	"error.account.groupFull": "This group is full.",
	"error.account.groupMemberNotFound": "This person is not in this group.",
	"error.account.lastMember": "That is the last member. Delete the group instead of removing them.",
	"error.account.notMentionable":
		"A group holds only active accounts of people — no bot, no deactivated account.",
	"error.account.tooManyMembers": "Too many members at once.",
	"error.account.groupBuiltin":
		"This group is built in, so it cannot be deleted or changed this way.",
	"error.account.groupAdminsOnly": "Only an administrator changes this group.",
	"error.account.groupMembershipAutomatic":
		"Everyone with an account is in this group automatically, so nobody is added or removed by hand.",
	"error.account.adminStaysInTeam":
		"An administrator is always a team member. Remove their administrator rights first.",

	// The administration's own refusals: the last admin, and the tokens of a bot account.
	"error.admin.lastAdminProtected":
		"That is the last account with administration rights. Appoint another one first.",
	"error.admin.tokenSubjectNotBot": "A token always belongs to a bot account.",
	"error.admin.tokenScopeInvalid": "A token has no such scope.",
	"error.admin.tokenCreatorMissing": "This token is missing the account that created it.",

	// The denial reasons of security.md §9.4, plus the two the admission chain refuses with. They
	// answer 404, and a private id is never an existence oracle: the copy must not turn into one.
	//
	// `not_member` may still name its reason, because it is also the reason an *unresolvable* ref is
	// refused with (internal/core/policy/conditions.go:191). A conversation that does not exist and
	// a private one this account is not in answer the same sentence, so the sentence can tell a
	// reader why their own message was refused — the case they actually meet, on a public channel
	// they have read but not joined (UX §8 #8) — without telling a stranger that anything is there.
	"error.policy.unauthenticated": "Please sign in again.",
	"error.policy.role": "You do not have permission for that.",
	"error.policy.not_member": "You are not a member of this conversation.",
	"error.policy.not_public": "Not found.",
	"error.policy.module_disabled": "Not found.",
	"error.policy.not_owner": "Only the person it belongs to may change that.",
	"error.policy.not_self": "That only works for your own account.",
	"error.policy.stepup_required": "For safety, this needs to be confirmed again.",
	"error.policy.code_required": "This also needs a code by e-mail.",
	"error.policy.archived": "This conversation is archived.",
	"error.policy.actor_disabled": "This account is deactivated.",
	"error.policy.killswitch": "This feature is switched off right now.",
	"error.policy.token_scope": "This token may not do that.",
	"error.policy.human_only": "That only works with a personal account.",
	// Claude acting in an AI chat, on a document whose *AI chats* level in Who can open is below the
	// act. A person never meets it; the page that shows Claude's refused call says it in their words.
	"error.policy.agent_level": "This document’s AI chats setting doesn’t allow that.",
	"error.policy.quota": "The quota is used up. Please try again later.",
	"error.policy.pending_missing": "The sign-in has expired. Please start again.",
	"error.policy.unknown_action": "Something went wrong.",
	"error.policy.csrf": "This request has expired. Please reload the page.",
	"error.policy.origin": "This request has expired. Please reload the page.",
} as const satisfies Record<string, string>
